<template>
    <DefaultTemplate :model="model">
        <div class="container">
            <div class="mt-12 mb-8" id="pagetop">
                <Breadcrumbs :breadcrumbs="model.breadcrumbs" className="text-grey-400" />
            </div>

            <div class="block lg:flex">
                <div class="w-full lg:w-3/4 pr-0 lg:pr-4">
                    <div v-if="model.image || epiContext.inEditMode">
                        <img v-epi-edit="'Image'" :src="model.image + mainImageParams" />
                    </div>

                    <article class="p-4 md:px-10 md:py-4 lg:px-20 lg:py-10 mb-4 bg-white">
                        <h1 v-epi-edit="'Name'" class="mb-2">{{ model.name }}</h1>
                        <div v-if="this.model.intro || this.model.mainBody">
                            <hr class="my-4 lg:my-10" />
                            <div v-epi-edit="'SecondaryBody'" class="main-body my-4 article" v-html="this.model.intro" />
                            <div v-epi-edit="'MainBody'" id="main-body" class="main-body my-4 article" v-html="this.model.mainBody" />
                        </div>
                    </article>

                    <!-- Mobile filter -->
                    <div class="sm:hidden flex flex-col justify-end items-end mb-4">
                        <button class="uppercase mb-2" @click="toggleMobileFilterOpen">Filtrer <font-awesome-icon icon="filter" /></button>
                        <div class="w-full ease-in duration-200 delay-75 overflow-hidden"
                             :class="[filterOpen ? 'max-h-96 transition-maxHeight' : 'max-h-0']">
                            <div class="p-5 mb-6 bg-white">
                                <dl class="text-sm">
                                    <dt v-epi-edit="'RightColumnHeading'" class="text-green-600 font-semibold pb-4">{{model.rightColumnHeading}}</dt>
                                    <dt>
                                        <ListFilter :allChildren="this.model.children" :currentChild="currentChild" @setFilterChild="setFilterChild" />
                                    </dt>
                                </dl>
                            </div>
                        </div>
                    </div>

                    <div v-if="dataLoaded">
                        <div v-if="items === null || items.length <= 0" class="text-warning">
                            Ups, ingenting å vise!
                        </div>

                        <div v-else>
                            <ul>
                                <li
                                    v-for="(item, index) in items"
                                    :key="$uuid.v4(index)"
                                    :class="[`border-${item.color}-500`]"
                                    class="flex mb-4 bg-white border-t-6 relative"
                                >
                                    <a
                                        :href="item.url"
                                        class="absolute top-0 left-0 h-full w-full"
                                        title="Les mer"
                                    >
                                        <span class="sr-only">
                                            Les mer
                                        </span>
                                    </a>
                                    <div
                                        class="w-1/4 py-4 md:py-0"
                                    >
                                        <img
                                            :src="`${item.image}?${getImageParams(item)}`"
                                            :alt="item.title"
                                        />
                                    </div>
                                    <div class="w-3/4 p-4">
                                        <h3>{{ item.title }}</h3>
                                        <div v-if="item.subHeading">
                                            <p class="text-sm pb-2" :inner-html.prop="item.subHeading" />
                                        </div>
                                        <div v-else>
                                            <p class="py-2 text-sm text-grey-300">[{{ new Date(item.publishDate).toLocaleDateString('nb-NO', dateOptions) }} Redaksjonen]</p>
                                            <p class="text-sm pb-2" :inner-html.prop="item.extract" />
                                        </div>
                                        <span v-if="item.fagfellevurdert === true" class="text-xs font-bold tracking-wider p-1 text-white rounded-sm bg-green-600">
                                            Fagfellevurdert artikkel
                                        </span>
                                    </div>
                                </li>
                            </ul>
                            <Pagination v-if="totalPages > 1" :currentPage="currentPage" :totalPages="totalPages" @change="getItems" />
                        </div>
                    </div>
                    <div v-else class="my-12">
                        <LoadingSpinner size="2x" />
                    </div>
                </div>
                <aside class="w-full lg:w-1/4 pr-0 lg:pl-4 mb-4">
                    <article-box v-if="epiContext.inEditMode" :header='`Ulike instillinger for denne siden`'>
                            <div>
                                <EpiProperty propertyName="color" displayText="temafarge for siden" />
                                <EpiProperty v-if="model.pageSize" propertyName="pageSize" displayText="antall elemeter per side" />
                                <EpiProperty propertyName="HideContentSearch" displayText="skjul i søk" />
                                <EpiProperty propertyName="HideContentFilter" displayText="skjul i filter" />
                                <EpiProperty propertyName="BildeIkkeValgt" displayText="bilde ikke valgt" />
                                <EpiProperty propertyName="BildeValgt" displayText="bilde valgt" />
                            </div>
                    </article-box>
                    <div class="p-5 mb-6 bg-white">
                        <dl class="text-sm">
                            <dt v-epi-edit="'RightColumnHeading'" class="text-green-600 font-semibold pb-4">{{model.rightColumnHeading}}</dt>
                            <dt>
                                <ListFilter :allChildren="this.model.children" :currentChild="currentChild" @setFilterChild="setFilterChild" />
                            </dt>
                        </dl>
                        <div class="flex justify-end mt-6">
                            <Sharing />
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    </DefaultTemplate>
</template>

<script>
import api from '@/Scripts/api/api';
import LoadingSpinner from '@/Scripts/components/atoms/LoadingSpinner/loadingSpinner.vue';
import Breadcrumbs from '@/Scripts/components/organisms/Breadcrumbs/breadcrumbs.vue';
import Sharing from '@/Scripts/components/atoms/Sharing/Sharing.vue';
import Pagination from '@/Scripts/components/organisms/Pagination/pagination.vue';
import ListFilter from '@/Scripts/components/organisms/Filters/listFilter.vue';
import ArticleBox from '@/Scripts/components/atoms/ArticleBox/ArticleBox.vue';
import EpiProperty from '@/Scripts/components/EpiProperty.vue';
import { mapGetters } from 'vuex';

export default {
    props: ['model'],
    components: {
        api,
        LoadingSpinner,
        Breadcrumbs,
        Sharing,
        Pagination,
        ListFilter,
        ArticleBox,
        EpiProperty
    },
    computed: {
        ...mapGetters({
            epiContext: 'getEpiContext'
        })
    },
    data() {
        const pageSize = (this.model.pageSize && this.model.pageSize.value ? this.model.pageSize.value : 10);
        return {
            dataLoaded: false,
            error: false,
            items: [],
            currentPage: 1,
            totalPages: 0,
            numPrPage: pageSize,
            imageParams: 'scale=both&width=180&height=112',
            dateOptions: { year: 'numeric', month: '2-digit', day: '2-digit' },
            currentChild: null,
            filterOpen: false,
            mainImageParams: '?scale=both&width=1112&maxheight=500&'
        };
    },
    created() {
        this.currentChild = this.model.children[0];
        this.getItems(this.currentPage);
    },
    methods: {
        setFilterChild(child) {
            this.filterOpen = false;
            this.currentChild = child;
            this.currentPage = 1;
            this.parentId = child.id;
            this.fetchAllDescendants = child.fetchAllDescendants;
            this.getItems(this.currentPage);
            sessionStorage.setItem('childId', child.id);
        },
        calculatePageCount(hitCount) {
            let count = hitCount / this.numPrPage;
            return Math.ceil(count > 0 ? count : 1);
        },
        getImageParams(item) {
            let params = 'scale=both&width=278&height=180';
            return (item.imageResizerParams ? `${params}&${item.imageResizerParams}` : params);
        },
        getItems(page, click = false) {
            this.dataLoaded = false;
            const parameters = {
                size: this.numPrPage,
                page: page,
                parentId: this.currentChild.id,
                fetchAllDescendants: this.currentChild.fetchAllDescendants
            };
            api.getPagesByParent(parameters).then(response => {
                if (response.status === 200) {
                    this.dataLoaded = true;
                    this.items = response.data.Hits;
                    this.totalPages = this.calculatePageCount(response.data.NumHitsPages);
                    this.currentPage = page;
                    if (click) {
                        document.getElementById('pagetop').scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
                    }
                } else {
                    this.error = true;
                }
            });
        },
        toggleMobileFilterOpen() {
            this.filterOpen = !this.filterOpen;
        },
    },
    mounted() {
        if (sessionStorage.childId) {
            this.currentChild = this.model.children.filter(x => {
                return x.id === parseInt(sessionStorage.getItem('childId'), 10) ? x : false;
            })[0];
            if (this.currentChild === undefined) {
                this.currentChild = this.model.children[0];
            }
            this.setFilterChild(this.currentChild);
        } else {
            this.currentChild = this.model.children[0];
            this.setFilterChild(this.currentChild);
        }
    },
};
</script>
